import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import moment from 'moment';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { IProgram } from 'src/app/models/IProgram';
import { IErrorMessage } from 'src/app/widgets/error-message/error-message.interface';
import { API_USER_DEFINED_MESSAGE } from 'src/app/widgets/error-message/error-message.const';
import { ProgramActions } from '../store/program.action';
import { ProgramState } from '../store/program.state';
@Component({
    selector: 'program-overview',
    templateUrl: './overview.component.html',
    styles: [],
    standalone: false
})
export class OverviewComponent implements OnInit {
    @Input() parentSpinner: boolean;
    @Output() sessionCount = new EventEmitter<number>();
    programId: string;
    program: IProgram;
    public programList: any[] = [];
    showLoading: boolean = false;
    private dateFormat: string = 'YYYY-MM-DD';
    private timeFormat: string = 'hh:mm A';
    public programOverviewErrorMessage: IErrorMessage = {};
    constructor(
        private store: Store,
        private route: ActivatedRoute
    ) { }
    ngOnInit(): void {
        this.clearStore();
        this.route.params.pipe(
            take(1),
            tap(params => {
                this.programId = params['id'];
                this.showLoading = true;
                this.programOverviewErrorMessage = {};
            }),
            switchMap(() =>
                // dispatch -> select slice -> take(1)
                forkJoin({
                    program: this.loadProgramOverview$(this.programId),
                    classes: this.loadClasses$(this.programId),
                    sessions: this.loadSessions$(this.programId)
                })
            ),
            finalize(() => {
                this.showLoading = false;
            })
        )
            .subscribe({
                next: ({ program, classes, sessions }) => {
                    this.program = program ?? ({} as IProgram);
                    this.sessionCount.emit(this.program?.counts?.events ?? 0);
                    this.buildProgramList(classes ?? [], sessions ?? []);
                },
                error: () => {
                    this.program = {} as IProgram;
                    this.programList = [];
                    this.programOverviewErrorMessage.text = API_USER_DEFINED_MESSAGE.FAILED;
                }
            });
    }
    private clearStore(): void {
        this.store.dispatch(new ProgramActions.ClearProgramOverview());
    }

    private loadProgramOverview$(id: string): Observable<IProgram> {
        return this.store.dispatch(new ProgramActions.GetProgramsOverview(id)).pipe(
            // after dispatch finishes, read state once
            switchMap(() => this.store.select(ProgramState.programOverview).pipe(take(1))),
            catchError(() => {
                this.programOverviewErrorMessage.text = API_USER_DEFINED_MESSAGE.FAILED;
                return of({} as IProgram);
            })
        );
    }
    private loadClasses$(id: string): Observable<any[]> {
        return this.store.dispatch(new ProgramActions.GetClassesByProgramId({ programId: id })).pipe(
            switchMap(() => this.store.select(ProgramState.getClasses).pipe(take(1))),
            map((classes: any) => Array.isArray(classes) ? classes : []),
            catchError(() => {
                this.programOverviewErrorMessage.text = API_USER_DEFINED_MESSAGE.FAILED;
                return of([]);
            })
        );
    }
    private loadSessions$(id: string): Observable<any[]> {
        return this.store.dispatch(new ProgramActions.GetSessionsByProgramId(id)).pipe(
            switchMap(() => this.store.select(ProgramState.getSessions).pipe(take(1))),
            map((sessions: any) => Array.isArray(sessions) ? sessions : []),
            catchError(() => {
                this.programOverviewErrorMessage.text = API_USER_DEFINED_MESSAGE.FAILED;
                return of([]);
            })
        );
    }

    private buildProgramList(classes: any[], sessions: any[]): void {
        const classesCopy = JSON.parse(JSON.stringify(classes ?? []));
        let sessionsCopy = JSON.parse(JSON.stringify(sessions ?? []));
        sessionsCopy = sessionsCopy.filter((x: any) => !x?.isCancelled);
        let events: any[] = [];
        events = events.concat(classesCopy.map((x: any) => {
            const startDate = moment(x.startDate);
            return {
                ...x,
                type: 'import_contacts',
                sDate: startDate.format(this.dateFormat),
                startTime: startDate.format(this.timeFormat),
                endTime: moment(x.endDate).format(this.timeFormat)
            };
        }));
        events = events.concat(sessionsCopy.map((x: any) => {
            const startDate = moment(x.startDate);
            return {
                ...x,
                type: 'local_play',
                sDate: startDate.format(this.dateFormat),
                startTime: startDate.format(this.timeFormat),
                endTime: moment(x.endDate).format(this.timeFormat)
            };
        }));
        const eventObj = events.reduce((r: any, a: any) => {
            r[a.sDate] = r[a.sDate] || [];
            r[a.sDate].push(a);
            return r;
        }, Object.create(null));
        const eventMap = new Map(Object.entries(eventObj));
        this.programList = Array.from(eventMap, ([key, value]) => ({ key, value }));
    }
}